import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";

// The owner's own pages — Overview, Accounts, Support, Platform settings —
// aren't inside any client, so they must never wear a client's color. Without
// this, they'd pick up whichever tenant the owner had open last: `tenantId`
// stays set (it's how "Open workspace" and the client switcher remember where
// you were) even once you've navigated back out to these platform-level
// screens, so the accent would otherwise leak in from the last account you
// visited instead of showing the platform's own neutral default.
//
// /profile is in this list too: Profile.jsx always renders the owner's own
// profile inside OwnerLayout (never the client Layout — "the owner has no
// client context"), so for the owner it's exactly as much a platform page as
// the other four, even though a client admin/agent reaching the same route
// gets their tenant's real color (isOwner gates this list, so that path is
// untouched).
const OWNER_ONLY_PATHS = ["/overview", "/accounts", "/platform", "/support-inbox", "/profile"];

const SettingsContext = createContext(null);

const DEFAULT_LABELS = {};

const FALLBACK = {
  modules: {},
  labels: DEFAULT_LABELS,
  business_name: "Workspace",
  brand_color: "#e17a1c",
  logo_url: "",
  font_family: "Plus Jakarta Sans",
  dark_mode: false,
  pin_policy: { length: 7, min_letters: 0 },
};

// Each offered accent color drives more than the --brand-* variables: the
// underlying shadcn primitives (buttons, checkboxes, switches, focus rings)
// read --primary/--ring/--secondary/--accent, so picking a swatch recolors
// the whole app, not just the surfaces styled with bg-brand utilities.
// Every value is an "H S% L%" triplet — the same format the rest of index.css's
// :root already uses — so tailwind.config.js can wrap each one in hsl(var(...))
// consistently. (A bare var(--x) with no hsl()/rgb() wrapper doesn't compile:
// Tailwind can't derive an opacity-modifier fallback for it.)
// Mirrors the ACCENT_SWATCHES list validated server-side in server.py.
const ACCENT_THEMES = {
  "#E17A1C": {
    brand: "29 78% 50%", brandHover: "29 78% 41%", brandLight: "29 35% 94%", brandRing: "29 45% 84%",
    primary: "29 78% 50%", ring: "29 78% 50%",
    secondary: "29 35% 94%", secondaryForeground: "29 40% 22%",
    accent: "29 35% 94%", accentForeground: "29 40% 22%",
  },
  "#1F6F5C": {
    brand: "166 56% 28%", brandHover: "166 56% 19%", brandLight: "166 35% 94%", brandRing: "166 45% 84%",
    primary: "166 56% 28%", ring: "166 56% 28%",
    secondary: "166 35% 94%", secondaryForeground: "166 40% 22%",
    accent: "166 35% 94%", accentForeground: "166 40% 22%",
  },
  "#7C5CBF": {
    brand: "259 44% 55%", brandHover: "259 44% 46%", brandLight: "259 35% 94%", brandRing: "259 44% 84%",
    primary: "259 44% 55%", ring: "259 44% 55%",
    secondary: "259 35% 94%", secondaryForeground: "259 40% 22%",
    accent: "259 35% 94%", accentForeground: "259 40% 22%",
  },
  "#4C86C9": {
    brand: "212 54% 54%", brandHover: "212 54% 45%", brandLight: "212 35% 94%", brandRing: "212 45% 84%",
    primary: "212 54% 54%", ring: "212 54% 54%",
    secondary: "212 35% 94%", secondaryForeground: "212 40% 22%",
    accent: "212 35% 94%", accentForeground: "212 40% 22%",
  },
  "#D9739A": {
    brand: "337 57% 65%", brandHover: "337 57% 56%", brandLight: "337 35% 94%", brandRing: "337 45% 84%",
    primary: "337 57% 65%", ring: "337 57% 65%",
    secondary: "337 35% 94%", secondaryForeground: "337 40% 22%",
    accent: "337 35% 94%", accentForeground: "337 40% 22%",
  },
  "#6FBF73": {
    brand: "123 38% 59%", brandHover: "123 38% 50%", brandLight: "123 35% 94%", brandRing: "123 38% 84%",
    primary: "123 38% 59%", ring: "123 38% 59%",
    secondary: "123 35% 94%", secondaryForeground: "123 38% 22%",
    accent: "123 35% 94%", accentForeground: "123 38% 22%",
  },
};

export const ACCENT_SWATCHES = Object.keys(ACCENT_THEMES);

/** Splits an "H S% L%" triplet (the format every ACCENT_THEMES value uses)
 * into its numeric parts so a dark-tinted variant can be derived from it. */
function parseHsl(triplet) {
  const [h, s, l] = triplet.split(" ").map((part) => parseFloat(part));
  return { h, s, l };
}

// Dark mode doesn't get a hand-authored palette per swatch — instead every
// accent color's dark variant is computed from its hue so adding a new
// swatch to ACCENT_THEMES automatically works in both themes. Brand gets
// lifted in lightness so it still reads against the near-black background;
// the "light"/ring tints become low-lightness washes instead of the
// light-mode's near-white ones.
function darkBrand({ h, s, l }) {
  return `${h} ${Math.max(s - 6, 30)}% ${Math.min(l + 18, 66)}%`;
}
function darkBrandHover({ h, s, l }) {
  return `${h} ${Math.max(s - 6, 30)}% ${Math.min(l + 26, 74)}%`;
}
function darkTintBg({ h }) {
  return `${h} 30% 16%`;
}
function darkRing({ h }) {
  return `${h} 30% 30%`;
}
function darkForeground({ h }) {
  return `${h} 25% 88%`;
}

/** Pushes one accent color's full derived palette onto the document root —
 * the light-mode palette baked into ACCENT_THEMES, or a dark-tinted variant
 * computed from the same hue when dark mode is on. */
function applyAccentColor(hex, isDark) {
  const theme = ACCENT_THEMES[(hex || "").toUpperCase()] || ACCENT_THEMES["#E17A1C"];
  const root = document.documentElement.style;
  if (isDark) {
    const hsl = parseHsl(theme.brand);
    const brand = darkBrand(hsl);
    const tint = darkTintBg(hsl);
    const fg = darkForeground(hsl);
    root.setProperty("--brand", brand);
    root.setProperty("--brand-hover", darkBrandHover(hsl));
    root.setProperty("--brand-light", tint);
    root.setProperty("--brand-ring", darkRing(hsl));
    root.setProperty("--primary", brand);
    root.setProperty("--ring", brand);
    root.setProperty("--secondary", tint);
    root.setProperty("--secondary-foreground", fg);
    root.setProperty("--accent", tint);
    root.setProperty("--accent-foreground", fg);
  } else {
    root.setProperty("--brand", theme.brand);
    root.setProperty("--brand-hover", theme.brandHover);
    root.setProperty("--brand-light", theme.brandLight);
    root.setProperty("--brand-ring", theme.brandRing);
    root.setProperty("--primary", theme.primary);
    root.setProperty("--ring", theme.ring);
    root.setProperty("--secondary", theme.secondary);
    root.setProperty("--secondary-foreground", theme.secondaryForeground);
    root.setProperty("--accent", theme.accent);
    root.setProperty("--accent-foreground", theme.accentForeground);
  }
}

/** Settings for whichever client account is currently open. */
const CACHE_PREFIX = "settings_cache:";

/** Last-known settings for a client account, so a refresh doesn't start from defaults. */
function readCache(tenantId) {
  if (!tenantId) return null;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + tenantId);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(tenantId, value) {
  if (!tenantId) return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + tenantId, JSON.stringify(value));
  } catch {
    /* private browsing */
  }
}

export function SettingsProvider({ children }) {
  const { user } = useAuth();
  const { tenantId, isOwner } = useTenant();
  const { pathname } = useLocation();
  const isPlatformChrome = isOwner && OWNER_ONLY_PATHS.includes(pathname);
  // Starting from the cached values means the sidebar doesn't briefly show
  // features this account has switched off.
  const [settings, setSettings] = useState(() => readCache(tenantId) || FALLBACK);
  const [loaded, setLoaded] = useState(() => !!readCache(tenantId));

  // Which client account the newest request was for. A reply that arrives
  // after we've moved on must be ignored — otherwise a slow answer for the
  // account you just left overwrites the one you're now in.
  const currentTenant = useRef(tenantId);

  // Switching account swaps to that account's cached settings straight away.
  useEffect(() => {
    currentTenant.current = tenantId;
    const cached = readCache(tenantId);
    setSettings(cached || FALLBACK);
    setLoaded(!!cached);
  }, [tenantId]);

  const refresh = useCallback(async () => {
    if (!user || !tenantId) {
      setSettings(FALLBACK);
      setLoaded(false);
      return;
    }
    const askedFor = tenantId;
    try {
      const s = await api.get("/settings");
      if (currentTenant.current !== askedFor) return;
      const merged = {
        ...FALLBACK,
        ...s.data,
        modules: { ...FALLBACK.modules, ...(s.data.modules || {}) },
        labels: { ...DEFAULT_LABELS, ...(s.data.labels || {}) },
      };
      setSettings(merged);
      writeCache(askedFor, merged);
      setLoaded(true);
    } catch {
      /* keep whatever we have so the shell still renders */
    }
  }, [user, tenantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Repaint the whole app the instant an accent color is known — from cache
  // on first paint, then again whenever the tenant switches or saves a new
  // swatch in Settings. Dark mode is folded in here too since the accent
  // palette itself changes shape between the two themes (see applyAccentColor).
  useEffect(() => {
    applyAccentColor(isPlatformChrome ? null : settings.brand_color, settings.dark_mode);
  }, [settings.brand_color, settings.dark_mode, isPlatformChrome]);

  // Dark mode toggles a single class on <html>; every color in index.css is
  // already var-driven off :root vs :root.dark, so nothing else has to react
  // to this beyond the accent recompute above.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", !!settings.dark_mode);
  }, [settings.dark_mode]);

  // The font picker swaps a single CSS var that tailwind.config.js's
  // fontFamily.sans reads first, ahead of the Plus Jakarta Sans fallback —
  // so every page repaints in the new typeface without per-page changes.
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--app-font",
      `'${settings.font_family || "Plus Jakarta Sans"}'`
    );
  }, [settings.font_family]);

  // Small helpers so screens don't each re-implement the same lookups.
  const isOn = (key) => settings.modules?.[key] !== false;
  const labelFor = (key) => settings.labels?.[key] || DEFAULT_LABELS[key] || key;

  return (
    <SettingsContext.Provider
      value={{ settings, setSettings, refresh, isOn, labelFor, loaded }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
